import moment from "moment";
import React, { useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";

import Button from "veneer/Button";
import Checkbox from "veneer/Checkbox";
import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import Stack from "veneer/Stack";

import { ROOT_URL } from "cortex/ui/src/fleet/constant/mediaLibrary";
import { contentGroupsSelector } from "cortex/ui/src/fleet/redux-store/content-groups/selectors";
import { archiveStrategyAction } from "cortex/ui/src/fleet/redux-store/strategies";
import {
  strategiesSelector,
  strategiesStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/strategies/selectors";
import StrategyDetails from "cortex/ui/src/fleet/view/strategy/component/StrategyDetails";
import { Strategy } from "cortex/ui/src/fleet/view/strategy/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import {
  applicationsSelector,
  applicationsStoresLoadedSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import { userContentsSelector } from "cortex/ui/src/redux-store/user-contents/selectors";
import {
  isAdmin,
  isOperator,
  isReadonly,
} from "cortex/ui/src/redux-store/users/selectors";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import { Container } from "cortex/ui/src/view/layout/Container";
import { VIEW } from "cortex/ui/src/view/views";

const getAllStrategiesAsArray = makeGetAllAsArray();

interface StrategyListProps {
  archived?: boolean;
  viewUrl?: string;
}

const filter = createFilter<Strategy>("name", "strategy");
const column = createColumnHelper<Strategy>();

export default function StrategyList({
  archived = false,
  viewUrl = `${ROOT_URL}/`,
}: StrategyListProps) {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const applications = useAppSelector(applicationsSelector);
  const applicationsLoaded = useAppSelector(applicationsStoresLoadedSelector);
  const contentGroups = useAppSelector(contentGroupsSelector);
  const userContents = useAppSelector(userContentsSelector);
  const strategyById = useAppSelector(strategiesSelector);
  const strategiesLoaded = useAppSelector(strategiesStoresLoadedSelector);
  const storesLoaded = useMemo(
    () => applicationsLoaded && strategiesLoaded,
    [applicationsLoaded, strategiesLoaded],
  );
  const user = useAppSelector(getAuthUser);
  const userIsAdmin = isAdmin(user);
  const userIsOperator = isOperator(user);
  const userIsReadonly = isReadonly(user);
  const [showArchivedOnly, setShowArchivedOnly] = useState<boolean>(archived);

  const rows = useMemo(() => {
    const all = getAllStrategiesAsArray({ byId: strategyById }) as Strategy[];

    const filtered = showArchivedOnly
      ? all.filter((s) => s.archived === true)
      : all.filter((s) => s.archived !== true);
    return filtered.map(
      (s): Strategy => ({
        ...s,
        strategy: { ...s.strategy, id: s.id },
      }),
    );
  }, [strategyById, showArchivedOnly]);

  const columns = useMemo(
    () => [
      column.accessor("name", {
        header: "Name",
        cell: (prop) => {
          const row = prop.row.original;
          const value = prop.getValue();
          return <Link to={`${viewUrl}${row.id}/${VIEW.EDIT}`}>{value}</Link>;
        },
        sortingFn: "alphanumeric",
      }),
      column.accessor("strategy", {
        header: "Strategy",
        cell: (prop) => {
          const strategyObj = prop.getValue();
          // Render full details & warnings (legacy parity)
          return (
            <StrategyDetails
              applications={applications}
              contentGroups={contentGroups}
              strategy={strategyObj}
              strategyId={prop.row.original.id}
              userContents={userContents}
            />
          );
        },
        enableSorting: false,
      }),
      column.accessor("create_date", {
        header: "Created At",
        cell: (prop) => formatDateTime(prop.getValue()),
        sortingFn: sorting.datetime((row) => row.create_date),
        meta: {
          width: 200,
        },
      }),
      column.display({
        id: "operations",
        header: "Operations",
        cell: (prop) => {
          if (!userIsAdmin && !userIsOperator) {
            return null;
          }
          return (
            <Stack alignItems="center" direction="row" spacing={1}>
              {userIsAdmin &&
                (!showArchivedOnly ? (
                  <Button
                    icon="archive"
                    onClick={() => handleArchiveStrategy(prop.row.original.id)}
                    size="small"
                    variant="outlined"
                  >
                    Archive
                  </Button>
                ) : (
                  <Button
                    icon="unarchive"
                    onClick={() => handleArchiveStrategy(prop.row.original.id)}
                    size="small"
                    variant="outlined"
                  >
                    Unarchive
                  </Button>
                ))}
              <Button
                icon="copyAll"
                onClick={() => handleCloneStrategy(prop.row.original.id)}
                size="small"
                variant="outlined"
              >
                Clone
              </Button>
            </Stack>
          );
        },
        meta: {
          width: 200,
        },
      }),
    ],
    [
      applications,
      contentGroups,
      showArchivedOnly,
      userContents,
      userIsAdmin,
      userIsOperator,
      viewUrl,
    ],
  );

  const handleArchiveStrategy = async (strategyId: number) => {
    if (!strategyId) return;
    const archiving = !showArchivedOnly;
    const successMessage = !archiving
      ? "Strategy unarchived successfully."
      : "Strategy archived successfully.";
    try {
      await dispatch(
        archiveStrategyAction(strategyId, archiving, successMessage),
      );
    } catch (e) {
      console.error("Failed to archive strategy:", e);
    }
  };

  const handleCloneStrategy = async (strategyId: number) => {
    if (!strategyId) return;
    const redirectTo = `${viewUrl}${strategyId}/${VIEW.CLONE}`;
    history.push(redirectTo);
  };

  const archiveToggle = (
    <Checkbox
      checked={showArchivedOnly}
      label="Show archived only"
      onChange={() => setShowArchivedOnly(!showArchivedOnly)}
    />
  );

  const addButton = !userIsReadonly ? (
    <Link to={`${viewUrl}${VIEW.CREATE}`}>
      <Button>Add Strategy</Button>
    </Link>
  ) : null;

  return (
    <Container title="Strategies">
      {storesLoaded ? (
        <DataTable
          columns={columns}
          filter={filter}
          hideColumns={!userIsAdmin && !userIsOperator ? ["operations"] : []}
          initialPageSize={10}
          initialSort={{ id: "create_date", desc: true }}
          primaryActions={addButton}
          rows={rows}
          viewOptions={archiveToggle}
        />
      ) : (
        <LoadingIndicator />
      )}
    </Container>
  );
}

const formatDateTime = (date: string): string => {
  if (!date) {
    return "N/A";
  }
  return moment(date).format("MMM Do, YYYY hh:mm:ss a");
};
